import React, { useId, useMemo, useCallback } from "react"
import { curveMonotoneX } from "@visx/curve"
import { AreaClosed, Line, Bar, LinePath } from "@visx/shape"
import { useTooltip } from "@visx/tooltip"
import { localPoint } from "@visx/event"
import { LinearGradient } from "@visx/gradient"
import { GridRows } from "@visx/grid"
import { ParentSize } from "@visx/responsive"
import type { TimeSeriesDataPoint, ChartMargin } from "./types"
import { CHART_COLORS, DEFAULT_MARGIN } from "./theme"
import { BucketUnit, getTimeX, getTimeY, getBisector } from "./utils/dataUtils"
import { makeBucketTickFormatter, makeBucketTooltipFormatter } from "./utils/formatters"
import { makeTimeScale, makeLinearScale } from "./utils/scales"
import { ChartTooltip } from "./components/ChartTooltip"
import { StandardAxisLeft, StandardAxisBottom } from "./components/ChartAxes"

export type ChartSeries = {
    key: string
    label: string
    color: string
    points: TimeSeriesDataPoint[]
}

export type MultiSeriesAreaChartProps = {
    series: ChartSeries[]
    unit: BucketUnit
    margin?: ChartMargin
    valueFormatter?: (value: number) => string
}

type InnerProps = MultiSeriesAreaChartProps & { width: number; height: number }

type HoveredBucket = {
    x: number
    values: { key: string; label: string; color: string; value: number }[]
}

const InnerChart = ({
    width,
    height,
    series,
    unit,
    margin = DEFAULT_MARGIN,
    valueFormatter = (value) => `${value}`,
}: InnerProps) => {
    const gradientId = useId()
    const { showTooltip, hideTooltip, tooltipData, tooltipLeft = 0 } = useTooltip<HoveredBucket>()

    const innerWidth = width - margin.left - margin.right
    const innerHeight = height - margin.top - margin.bottom

    const xPoints = useMemo(() => series[0]?.points ?? [], [series])
    const allPoints = useMemo(() => series.flatMap((s) => s.points), [series])

    const xScale = useMemo(
        () =>
            makeTimeScale({
                data: xPoints,
                accessor: getTimeX,
                rangeStart: margin.left,
                rangeEnd: innerWidth + margin.left,
            }),
        [xPoints, margin.left, innerWidth]
    )

    const yScale = useMemo(
        () =>
            makeLinearScale({
                data: allPoints,
                accessor: getTimeY,
                rangeStart: innerHeight + margin.top,
                rangeEnd: margin.top,
                minValue: 1,
            }),
        [allPoints, margin.top, innerHeight]
    )

    const tickValues = useMemo(() => {
        const maxTicks = Math.max(1, Math.floor(innerWidth / 72))
        const stride = Math.ceil(xPoints.length / maxTicks)
        return xPoints
            .filter((_, i) => (xPoints.length - 1 - i) % stride === 0)
            .map((d) => new Date(getTimeX(d)))
    }, [xPoints, innerWidth])

    const tickFormatter = useMemo(() => makeBucketTickFormatter(unit), [unit])
    const tooltipDateFormatter = useMemo(() => makeBucketTooltipFormatter(unit), [unit])

    const handleTooltip = useCallback(
        (event: React.TouchEvent<SVGRectElement> | React.MouseEvent<SVGRectElement>) => {
            if (!xPoints.length) return
            const { x } = localPoint(event) || { x: 0 }
            const x0 = xScale.invert(x).getTime()
            const index = getBisector(xPoints, x0, getTimeX)
            const before = xPoints.at(index - 1)
            const after = xPoints.at(index)
            const nearest = !before
                ? after
                : !after
                  ? before
                  : x0 - getTimeX(before) > getTimeX(after) - x0
                    ? after
                    : before
            if (!nearest) return

            const bucketX = getTimeX(nearest)
            showTooltip({
                tooltipData: {
                    x: bucketX,
                    values: series.map((s) => ({
                        key: s.key,
                        label: s.label,
                        color: s.color,
                        value: getTimeY(s.points.find((p) => p.x === bucketX)),
                    })),
                },
                tooltipLeft: xScale(bucketX),
                tooltipTop: margin.top,
            })
        },
        [showTooltip, xScale, xPoints, series, margin.top]
    )

    if (width < 10) return null

    return (
        <div style={{ position: "relative", width, height }}>
            <svg width={width} height={height}>
                <rect width={width} height={height} fill={CHART_COLORS.background} rx={4} />

                {series.map((s) => (
                    <LinearGradient
                        key={s.key}
                        id={`${gradientId}-${s.key}`}
                        from={s.color}
                        to={s.color}
                        fromOpacity={0.25}
                        toOpacity={0}
                    />
                ))}

                <GridRows
                    left={margin.left}
                    scale={yScale}
                    width={innerWidth}
                    stroke={CHART_COLORS.grid}
                    strokeOpacity={CHART_COLORS.gridOpacity}
                    pointerEvents="none"
                />

                <StandardAxisLeft scale={yScale} left={margin.left} numTicks={6} />
                <StandardAxisBottom
                    scale={xScale}
                    top={innerHeight + margin.top}
                    tickFormat={tickFormatter}
                    tickValues={tickValues}
                />

                {series.map((s) => (
                    <g
                        key={`${s.key}-${unit}`}
                        className="motion-safe:animate-bar-grow origin-bottom [transform-box:fill-box]"
                    >
                        <AreaClosed<TimeSeriesDataPoint>
                            data={s.points}
                            x={(d) => xScale(getTimeX(d))}
                            y={(d) => yScale(getTimeY(d))}
                            yScale={yScale}
                            fill={`url(#${gradientId}-${s.key})`}
                            curve={curveMonotoneX}
                        />
                        <LinePath<TimeSeriesDataPoint>
                            data={s.points}
                            x={(d) => xScale(getTimeX(d))}
                            y={(d) => yScale(getTimeY(d))}
                            stroke={s.color}
                            strokeWidth={1.5}
                            curve={curveMonotoneX}
                        />
                    </g>
                ))}

                <Bar
                    x={margin.left}
                    y={margin.top}
                    width={innerWidth}
                    height={innerHeight}
                    fill="transparent"
                    onMouseMove={handleTooltip}
                    onTouchMove={handleTooltip}
                    onMouseLeave={() => hideTooltip()}
                />

                {tooltipData && (
                    <g>
                        <Line
                            from={{ x: tooltipLeft, y: margin.top }}
                            to={{ x: tooltipLeft, y: innerHeight + margin.top }}
                            stroke={CHART_COLORS.label}
                            strokeWidth={1}
                            pointerEvents="none"
                        />
                        {tooltipData.values.map((v) => (
                            <circle
                                key={v.key}
                                cx={tooltipLeft}
                                cy={yScale(v.value)}
                                r={3.5}
                                fill={v.color}
                                stroke={CHART_COLORS.background}
                                strokeWidth={2}
                                pointerEvents="none"
                            />
                        ))}
                    </g>
                )}
            </svg>

            {tooltipData && (
                <ChartTooltip top={margin.top} left={tooltipLeft}>
                    <div className="flex flex-col gap-1">
                        <div className="whitespace-nowrap text-white">
                            {tooltipDateFormatter.format(new Date(tooltipData.x))}
                        </div>
                        <div className="grid grid-cols-[auto_auto_auto] items-center gap-x-2 gap-y-1">
                            {tooltipData.values.map((v) => (
                                <React.Fragment key={v.key}>
                                    <div
                                        className="w-1.5 h-1.5 shrink-0 rounded-full"
                                        style={{ backgroundColor: v.color }}
                                    />
                                    <span className="whitespace-nowrap text-gray-300">
                                        {v.label}
                                    </span>
                                    <span className="mono whitespace-nowrap text-white justify-self-end">
                                        {valueFormatter(v.value)}
                                    </span>
                                </React.Fragment>
                            ))}
                        </div>
                    </div>
                </ChartTooltip>
            )}
        </div>
    )
}

export const MultiSeriesAreaChart = (props: MultiSeriesAreaChartProps) => (
    <div style={{ width: "100%", height: "100%" }}>
        <ParentSize>
            {({ width, height }) => <InnerChart {...props} width={width} height={height} />}
        </ParentSize>
    </div>
)
